const memoryCache = new Map();
const inflightRequests = new Map();
const failureBackoffUntil = new Map();

/** Default TTL for list/content APIs. */
export const CACHE_TTL = 2 * 60 * 1000;
/** Longer TTL for session/profile/wallet data that rarely changes mid-navigation. */
export const SESSION_CACHE_TTL = 5 * 60 * 1000;
/** Short pause after a 429 so tab switching does not keep hammering the API. */
const FAILURE_BACKOFF_MS = 10 * 1000;

export const CACHE_KEYS = {
  userInfo: "user:info",
  userProfile: "user:profile",
  activeStreak: "streak:active",
  challenges: "challenges:list",
  mythFact: "myth-fact:current",
  easters: "economy:easters",
  eslGamesCheck: "esl-games:check",
};

function storageKey(key) {
  return `ga_api_cache_${key}`;
}

function readStoredEntry(key) {
  try {
    const raw = sessionStorage.getItem(storageKey(key));
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || entry.value === undefined) return null;
    return entry;
  } catch {
    return null;
  }
}

/** Fresh (within TTL) value from sessionStorage. Expired entries are kept for stale fallback. */
function readStored(key, ttl) {
  const entry = readStoredEntry(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > ttl) return null;
  return entry.value;
}

/** Any previously stored value, including expired (stale-while-revalidate). */
function readStaleStored(key) {
  const entry = readStoredEntry(key);
  return entry?.value ?? null;
}

function getStaleValue(key) {
  const memoryEntry = memoryCache.get(key);
  if (memoryEntry?.value !== undefined) return memoryEntry.value;
  return readStaleStored(key);
}

function writeStored(key, value) {
  try {
    const storedValue =
      value && value.data !== undefined
        ? {
            data: value.data,
            status: value.status,
            statusText: value.statusText,
            headers: value.headers,
          }
        : value;
    sessionStorage.setItem(
      storageKey(key),
      JSON.stringify({ timestamp: Date.now(), value: storedValue }),
    );
  } catch {
    // Session storage can be unavailable or full; memory caching still works.
  }
}

/**
 * Deduped GET/POST helper with memory + optional sessionStorage cache.
 * On 429 / backoff, serves last-known good (stale) data when available.
 * @param {string} key
 * @param {() => Promise<any>} request
 * @param {{ ttl?: number, force?: boolean, persist?: boolean }} [options]
 *   persist: false keeps an in-memory cache only (no sessionStorage).
 */
const cacheGeneration = new Map();

function bumpGeneration(key) {
  const next = (cacheGeneration.get(key) || 0) + 1;
  cacheGeneration.set(key, next);
  return next;
}

export function cachedRequest(key, request, options = {}) {
  const ttl = options.ttl ?? CACHE_TTL;
  const force = options.force === true;
  const persist = options.persist !== false;

  if (force) {
    // Share an in-flight refresh so Layout + page (or duplicate mounts)
    // do not each open a parallel network call for the same key.
    const inflightForce = inflightRequests.get(key);
    if (inflightForce) return inflightForce;

    // Clear cached values only — keep the inflight slot we are about to set.
    memoryCache.delete(key);
    failureBackoffUntil.delete(key);
    bumpGeneration(key);
    try {
      sessionStorage.removeItem(storageKey(key));
    } catch {
      // Ignore unavailable session storage.
    }
  } else {
    const memoryEntry = memoryCache.get(key);
    if (memoryEntry && Date.now() - memoryEntry.timestamp <= ttl) {
      return Promise.resolve(memoryEntry.value);
    }

    if (persist) {
      const storedValue = readStored(key, ttl);
      if (storedValue) {
        memoryCache.set(key, { timestamp: Date.now(), value: storedValue });
        return Promise.resolve(storedValue);
      }
    }

    const backoffUntil = failureBackoffUntil.get(key) || 0;
    if (Date.now() < backoffUntil) {
      const stale = getStaleValue(key);
      if (stale !== null && stale !== undefined) {
        return Promise.resolve(stale);
      }
      const err = new Error(`Rate-limit backoff active for ${key}`);
      err.code = "ERR_RATE_LIMIT_BACKOFF";
      return Promise.reject(err);
    }

    if (inflightRequests.has(key)) return inflightRequests.get(key);
  }

  const generation = cacheGeneration.get(key) || 0;

  const pending = Promise.resolve()
    .then(request)
    .then((value) => {
      // Ignore late writes from requests invalidated while in flight.
      if ((cacheGeneration.get(key) || 0) === generation) {
        memoryCache.set(key, { timestamp: Date.now(), value });
        if (persist) writeStored(key, value);
        failureBackoffUntil.delete(key);
      }
      return value;
    })
    .catch((error) => {
      if (error?.response?.status === 429) {
        if ((cacheGeneration.get(key) || 0) === generation) {
          failureBackoffUntil.set(key, Date.now() + FAILURE_BACKOFF_MS);
        }
        const stale = getStaleValue(key);
        if (stale !== null && stale !== undefined) {
          return stale;
        }
      }
      throw error;
    })
    .finally(() => {
      if (inflightRequests.get(key) === pending) {
        inflightRequests.delete(key);
      }
    });

  inflightRequests.set(key, pending);
  return pending;
}

export function invalidateCachedRequest(key) {
  memoryCache.delete(key);
  failureBackoffUntil.delete(key);
  bumpGeneration(key);
  // Drop in-flight handle so a forced refresh cannot join a pre-claim GET.
  inflightRequests.delete(key);
  try {
    sessionStorage.removeItem(storageKey(key));
  } catch {
    // Ignore unavailable session storage.
  }
}

export function invalidateCachedRequests(keys = []) {
  keys.forEach((key) => invalidateCachedRequest(key));
}

/** Drop bloated / redundant keys left from older builds. Safe at boot. */
export function purgeBloatedSessionCaches() {
  try {
    const remove = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (!key) continue;
      if (key === "eggCount") remove.push(key);
      else if (key.startsWith("ga_en2_") || key.startsWith("ga_ru2_"))
        remove.push(key);
      else if (key.startsWith("ga_api_cache_podcasts:view:")) remove.push(key);
      else if (key.startsWith("ga_api_cache_textual-content:")) remove.push(key);
    }
    remove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Session storage can be unavailable.
  }
}
